/**
 * AgendaItem.js
 * This component is loaded for each item found in Agenda.js
 *  - The information on whether to show/hide the columns is calculated in the parent
 *  - The numbering information is calculated in the parent and deconstructed as needed (renderNumbering)
 *  - The docs are ordered before displaying and marked as Read on click of the link
 *  - When a doc is marked as Read, the local storage check is made to see if the meeting itself can be marked as Read
 */
import React from 'react';
import PropTypes from 'prop-types';
import * as local from '../../../../helpers/localStorage';
import store from '../../../../stores/store';
import * as utils from '../../../../helpers/utils';
import { DOC_TYPE_AGENDA, HTML_DOC_TYPE } from '../../../../helpers/constants';
import { Online, Offline } from '../../../_shared/conn';
import SaveAsButton from './SaveAsButton';

class AgendaItem extends React.Component {

    constructor() {
        super()
        this.state = {
            downloadDropdown: false
        }
    }

    toggleDownloadDropdown = () => {
        this.setState({
            downloadDropdown: !this.state.downloadDropdown
        })
    }

    renderDocLink = detail => {
        //read:     <a class="icon-download-success" href="#.xls">Presentation_spreadsheet - introductory tasks</a><br /><small class="note-icon">15 notes</small>
        //unread:   <a class="doc-unread" href="#.ppt">Presentation_slides-part_05 - conclusion notes</a>
        //downloading:
        // <a class="" href="#.ppt">Presentation_slides-part_03 - progress report</a><span class="doc-unread"></span>
        // <div class="progress">
        //     <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-valuenow="50" aria-valuemin="0" aria-valuemax="100" style="width: 50%">50%</div>
        // </div>

        const instanceId = store.companies.selectedId;
        const meetingId = this.props.details.MeetingId;
        const key = local.LS_AGENDAS + instanceId + meetingId + store.user.profile[0].UserId;
        const keyDownloaded =
            local.LS_AGENDAS_DOWNLOADED + instanceId + meetingId + store.user.profile[0].UserId;
        const isRead = local.isKeyExists(key, detail.AgendaDocId);
        const isDownloaded = local.isKeyExists(keyDownloaded, detail.AgendaDocId);
        // console.log('AgendaItem renderDocLink', detail);\

        return (
            <div>
            <div target="_blank" onClick={this.openDocument.bind(this, key, detail)}>
                <div
                    className={`doc-clickable-block ${isRead ? '' : 'doc-unread'} ${
                        isDownloaded ? 'icon-download-success' : ''
                    }`}
                    title={'.' + detail.DocExtension}
                >
                    {detail.DocName}
                </div>
                {this.notesRender(detail)}
            </div>
                <SaveAsButton document={detail} docTypeId={DOC_TYPE_AGENDA} />
                </div>
        );
    };


    onLinkClick = detail => {
        // console.log('AgendaItem onLinkClick');
        const AgendaDocId = detail.AgendaDocId;
        const instanceId = this.props.instanceId;
        const groupId = this.props.groupId;
        const meetingId = this.props.meeting.MeetingId;
        const key = local.LS_AGENDAS + instanceId + meetingId + store.user.profile[0].UserId;
        const keyDownloaded =
            local.LS_AGENDAS_DOWNLOADED + instanceId + meetingId + store.user.profile[0].UserId;

        // console.log('AgendaItem onLinkClick', detail.AgendaDocId, this.props);  //AgendaDocId, key)

        local.saveNumberByKeyStringInLocalStorageArray(key, AgendaDocId);
        local.saveNumberByKeyStringInLocalStorageArray(keyDownloaded, AgendaDocId);
        local.setMeetingAsRead(instanceId, groupId, meetingId, utils.getNowInUtc());

        store.documents.logDocumentRead(instanceId, AgendaDocId, DOC_TYPE_AGENDA);

        this.refreshUnreadList();
    };

    renderHtmlLink = detail => {
        const instanceId = store.companies.selectedId;
        const meetingId = this.props.details.MeetingId;
        const key = local.LS_AGENDAS + instanceId + meetingId + store.user.profile[0].UserId;
        const isRead = local.isKeyExists(key, detail.AgendaDocId);

        return (
            <div>
                <Online>
                    <a
                        href={utils.checkURL(detail.DocUrl)}
                        target="__blank"
                        title={'.com'}
                        className={`doc-clickable-block ${isRead ? '' : 'doc-unread'}`}
                        onClick={this.onLinkClick.bind(this, detail)}
                    >
                        {detail.DocName}
                    </a>
                </Online>
                <Offline>
                    <a title="! OFFLINE" className={`doc-clickable-block ${isRead ? '' : 'doc-unread'}`}>
                        {detail.DocName}
                    </a>
                </Offline>
            </div>
        );
    };

    refreshUnreadList = async () => {
        if (await store.conn.checkOnlineStatus()) {
            //store.meetings.fetch();
            store.meetings.fetchCurrentGroup();          
        }
    };

    openDocument = (key, details) => {
        const AgendaDocId = details.AgendaDocId;
        const DocExtension = details.DocExtension;
        const DocPermissions = details.DocPermissions;
        const DocName = details.DocName;
        const DocAnnotations = details.DocAnnotations;

        const instanceId = this.props.instanceId;
        const groupId = this.props.groupId;
        const meetingId = this.props.details.MeetingId;
        const keyDownloaded =
            local.LS_AGENDAS_DOWNLOADED + instanceId + meetingId + store.user.profile[0].UserId;

        // console.log('Governance onDocumentClick', instanceId);
        store.documents
            .fetchDocument({
                instanceId,
                docTypeId: DOC_TYPE_AGENDA,
                docId: AgendaDocId,
                DocExtension,
                DocPermissions,
                DocName,
                DocAnnotations,
                onClose: this.props.onToggle,
            })
            .then(res => {
                if (res) {
                    // console.log('AgendaItem openDocument', this.props.details);
                    local.saveNumberByKeyStringInLocalStorageArray(key, AgendaDocId);
                    local.saveNumberByKeyStringInLocalStorageArray(keyDownloaded, AgendaDocId);
                    local.setMeetingAsRead(instanceId, groupId, meetingId, utils.getNowInUtc());

                    store.documents.logDocumentRead(instanceId, AgendaDocId, DOC_TYPE_AGENDA);
                }
            })
            .catch(error => {
                console.log('error fetching doc - agenda');
            });

        this.refreshUnreadList();
    };

    notesRender = detail => {
        if (!detail.DocAnnotations) return null;
        let notes = detail.DocCommentCount ? `${detail.DocCommentCount} notes` : 'Notes';

        return <small className="note-icon">{notes}</small>;
    };

    renderAgendaUnreadByHeader = details => {
        if (store.conn.isOnline) {
            if (this.props.displayList === 1) {
                let agendaDocsUnreadBy = Array.from(details.AgendaDocsUnreadBy);
                var noOfRecords = agendaDocsUnreadBy.length;

                agendaDocsUnreadBy.sort((a, b) => {
                    return a.Firstname - b.Firstname;
                });

                if (agendaDocsUnreadBy.length > 0) {
                    return (
                        <div>
                            <div className="small text-muted unread-by-label">
                                Unopened by: {noOfRecords} {noOfRecords === 1 ? 'user' : 'users'}
                            </div>
                            <div className="unread-user-list">
                                {agendaDocsUnreadBy.map((detail, i) => (
                                    <div key={i}>
                                        {detail.Firstname} {detail.Surname}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }
            }
        }
    };

    renderDocList = details => {
        let agendaDocs = Array.from(details.AgendaItemDocs);

        agendaDocs.sort((a, b) => {
            return a.Position - b.Position;
        });

        return agendaDocs.map((detail, i) => (
            <div key={i} className="doc-only-first-child">
                {detail.DocType === HTML_DOC_TYPE
                    ? this.renderHtmlLink(detail)
                    : this.renderDocLink(detail)}

                {this.renderAgendaUnreadByHeader(detail)}
            </div>
        ));
    };

    renderNumbering = numbering => {
        const { nomenclature } = this.props;
        let formattedNumbering = '';
        const arrNumbering = numbering.split('.');

        for (let i = 1; i < arrNumbering.length; i++) {
            formattedNumbering =
                formattedNumbering + nomenclature['delimiter'] + nomenclature[i][arrNumbering[i]];
        }

        return nomenclature['delimiter'].length === 0
            ? formattedNumbering
            : formattedNumbering.substr(1);
    };

    render() {
        const { details, meeting, numberingType } = this.props;
        const showColumns = store.meetings.agendaTable.showColumns;

        const breakStyle = details.Break ? 'break-background' : '';

        return (
            <tr className={breakStyle}>
                {showColumns.numbering ? (
                    <td data-title="Item number: " scope="row">
                    {numberingType === 3 ? details.ItemNumberString :
                     this.renderNumbering(details.IndrasNumbering)}                      
                    </td>
                ) : null}
                <td data-title="Title &amp; Description: ">
                    {details.ItemTitle}
                    <br />
                    <small style={{ whiteSpace: 'pre-line' }}>{details.ItemDescription}</small>
                    {/* <pre >{details.ItemDescription}</pre> */}
                </td>

                {showColumns.category ? <td data-title="Category: ">{details.Category}</td> : null}
                {showColumns.presenter ? (
                    <td data-title="Presenter: ">{details.Presenter}</td>
                ) : null}
                {showColumns.duration ? (
                    <td data-title="Time: ">
                        {utils.getFormattedLocalDatetime(
                            details.StartTime,
                            meeting.TimeOffset,
                            'h:mm a',
                        )}
                    </td>
                ) : null}
                {showColumns.documents ? (
                    <td data-title="Documents: ">{this.renderDocList(details)}</td>
                ) : null}
            </tr>
        );
    }
}

AgendaItem.propTypes = {
    details:        PropTypes.object.isRequired,
    nomenclature:   PropTypes.object.isRequired,
    groupId:        PropTypes.number.isRequired,
    instanceId:     PropTypes.number.isRequired,
    onToggle:       PropTypes.func,
    numberingType:  PropTypes.number.isRequired,
};

export default AgendaItem;



// WEBPACK FOOTER //
// ./src/containers/PrivateArea/Meeting/components/AgendaItem.js